<script setup lang="ts">
import { ZF_NO } from '@mp/constant'
import { nextTick, ref } from 'vue'
import { useScriptTag } from '@vueuse/core'
import { getDeviceCode } from '@mp/utils'

const emit = defineEmits<{
  (e: 'success', value: string): void
}>()

useScriptTag(
  `${import.meta.env.MODE === 'development' ? '/' : '/merchant-platform/'}tac/load.min.js`,
)

declare global {
  interface Window {
    initTAC: (path: string, config: any, style: any) => Promise<any>
    currentCaptcha?: {
      currentCaptchaData?: unknown
    }
  }
}

const dialogVisible = ref<boolean>(false)
const tacInstance = ref<any>(undefined)
const captchaBoxId = ref<string>(`captcha-box-${Date.now()}`)

/** 判断验证码容器在当前弹窗中是否仍然挂载。 */
function isCaptchaBoxMounted() {
  return dialogVisible.value && Boolean(document.getElementById(captchaBoxId.value))
}

/** 验证码数据未就绪时，阻止 TAC 的拖动事件访问空数据。 */
function handleSliderStart(event: Event) {
  if (window.currentCaptcha?.currentCaptchaData)
    return

  event.preventDefault()
  event.stopImmediatePropagation()
}

async function init() {
  const config = {
    // 生成接口 (必选项,必须配置, 要符合tianai-captcha默认验证码生成接口规范)
    requestCaptchaDataUrl: `${import.meta.env.MODE === 'development' ? '' : import.meta.env.VITE_SDPMCHAUTH}/v4/captcha/getDifficultCaptcha`,
    // 验证接口 (必选项,必须配置, 要符合tianai-captcha默认验证码校验接口规范)
    validCaptchaUrl: `${import.meta.env.MODE === 'development' ? '' : import.meta.env.VITE_SDPMCHAUTH}/v4/captcha/checkDifficultCaptcha`,
    // 验证码绑定的div块 (必选项,必须配置)
    bindEl: `#${captchaBoxId.value}`,

    requestHeaders: {
      appId: ZF_NO,
      deviceId: await getDeviceCode(),
    },
    // 验证成功回调函数(必选项,必须配置)
    validSuccess: (res: any, c: any, tac: any) => {
      tacInstance.value = tac
      // 销毁验证码服务
      tac.destroyWindow()
      dialogVisible.value = false

      if (!res?.data)
        return
      emit('success', res.data)
    },
    // 验证失败的回调函数(可忽略，如果不自定义 validFail 方法时，会使用默认的)
    validFail: (_res: any, _c: any, tac: any) => {
      // 验证失败后重新拉取验证码
      if (isCaptchaBoxMounted())
        tac.reloadCaptcha()
    },
    // 刷新按钮回调事件
    btnRefreshFun: (_el: any, tac: any) => {
      tac.reloadCaptcha()
    },
    // 关闭按钮回调事件
    btnCloseFun: (_el: any, tac: any) => {
      tac.destroyWindow()
      dialogVisible.value = false
    },
  }
  // 一些样式配置， 可不传
  const style = {
    logoUrl: null, // 去除logo
    moveTrackMaskBgColor: 'var(--el-color-primary)',
    moveTrackMaskBorderColor: 'var(--el-color-primary)',
  }
  if (!isCaptchaBoxMounted())
    return false

  // 参数1 为 tac文件是目录地址， 目录里包含 tac的js和css等文件
  // 参数2 为 tac验证码相关配置
  // 参数3 为 tac窗口一些样式配置
  window.initTAC(`${import.meta.env.MODE === 'development' ? '/tac' : '/merchant-platform/tac'}`, config, style).then((tac) => {
    if (!isCaptchaBoxMounted())
      return
    tac.init() // 调用init则显示验证码
  }).catch((e: any) => {
    console.warn('初始化tac失败', e)
  })

  return true
}

defineExpose({
  async open() {
    dialogVisible.value = true
    await nextTick()
    init()

    return true
  },
})
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :width="400" :close-on-press-escape="false" :close-on-click-modal="false"
    style="--el-dialog-padding-primary: 0; --el-dialog-bg-color: transparent" append-to-body :show-close="false"
  >
    <div :id="captchaBoxId" class="flex justify-center items-center" @mousedown.capture="handleSliderStart" @touchstart.capture="handleSliderStart" />
  </el-dialog>
</template>
